
import { Component, Vue, Prop } from 'vue-property-decorator';
import Installing from '@/components/Installing.vue';
import { UserPackageModel } from '@/models/user_package';
import { BillingPackageList } from '@/collections/billing_packages';
import { StoreModel } from '@/models/store';
import { isEmbedded, redirectExternal, redirectApp } from '@/utils/shopifyBridge';
import { EventHandler } from '@/modules/events';
import { EventBus } from '@/main';
import { PricingPageEventHandler } from '@/modules/pricingPageEvents';

@Component({
  components: {
    Installing,
  },
})
export default class SubscriptionConfirm extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  public userPackage: UserPackageModel = new UserPackageModel();
  public currentPackage: UserPackageModel = new UserPackageModel();
  public hasError: boolean = false;
  public submitting: boolean = false;
  @Prop({default: null})
    public app!: any;
  // Analytics tracker for payment confirmation flow
  private pricingTracker: PricingPageEventHandler | null = null;
  private confirmationStartTime: number = 0;

  /**
   * Safely extract error message from unknown error type
   *
   * Performs runtime type checking to extract error messages without unsafe casts.
   * Handles various error formats: Error instances, objects with message property,
   * and other types.
   *
   * @param error - The error to extract message from (unknown type)
   * @returns {string} The error message or 'UNKNOWN' if extraction fails
   *
   * @example
   * ```typescript
   * try {
   *   await riskyOperation();
   * } catch (error) {
   *   const errorMsg = this.extractErrorMessage(error);
   * }
   * ```
   */
  private extractErrorMessage(error: unknown): string {
    // Check if it's an Error instance
    if (error instanceof Error) {
      return error.message;
    }

    // Check if it's an object with a message property
    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as Record<string, unknown>).message;
      return message ? String(message) : 'UNKNOWN';
    }

    // Try to convert to string, fallback to 'UNKNOWN'
    try {
      return error ? String(error) : 'UNKNOWN';
    } catch {
      return 'UNKNOWN';
    }
  }

  public async created() {
    // Initialize pricing tracker
    this.pricingTracker = new PricingPageEventHandler();
    this.confirmationStartTime = Date.now();

    this.userPackage.chargeId = Number(this.$route.query.charge_id);
    if (this.userPackage.chargeId) {
      // Track payment return from Shopify (non-blocking)
      try {
        if (this.pricingTracker) {
          this.pricingTracker.trackPaymentReturn(
            this.userPackage.chargeId,
            this.userPackage.id?.toString()
          );
        }
      } catch (trackingError) {
        // Silently fail - never block confirmation flow
        console.warn('[SubscriptionConfirm] Payment return tracking failed:', trackingError);
      }

      await this.confirmSubscription();
    } else {
      const billingPackages: BillingPackageList = new BillingPackageList();
      await billingPackages.fetch();
      for (const item of billingPackages.items) {
        if (item.price === 0) {
          this.userPackage.billingPackageId = item.id;
          break;
        }
      }
      let returnUrl = `${process.env.VUE_APP_SUBSCRIPTION_RETURN_URL}`;
      if (isEmbedded()) {
        returnUrl = `https://admin.shopify.com/store/${
          this.store.shopName.split('.')[0]
        }/apps/${process.env.VUE_APP_NAME}/subscription/confirm`;
      }
      try {
        await this.currentPackage.getCurrent();
      } catch {
        // skipped
      }
      if (!this.currentPackage.id) {
        await this.userPackage.create(0, 0, returnUrl);
      }
      try {
        const eventHandler = new EventHandler({
          store: this.store,
        });
        await eventHandler.track('STOP_CONFIRMING_PAID_PACKAGE', {
          billing_package_id: this.userPackage.billingPackageId,
        });
        await this.store.sendEvent('STOP_CONFIRMING_PAID_PACKAGE', {
          billing_package_id: this.userPackage.billingPackageId,
        });
      } catch (e) {
        // ignore this
      }
      // Charge-abandoner path: the merchant came back from Shopify without a
      // charge_id, so nothing has marked onboarding complete yet. Without this
      // PUT, passed_installing_flow stays 'new' and App.vue's bootstrap guard
      // (App.vue:929 — 'atlasAgent' is not in installationUrls) pushes them
      // back to /onboard-pricing on the next app open. Under managed install
      // every open re-runs that guard, so it never resolves. Left unguarded to
      // match the paid path (confirmSubscription) — a silent catch here would
      // land the merchant in exactly the loop this prevents.
      await this.store.updateInstallationStatus();
      redirectApp('/assistant/onboarding');
    }
  }

  public async retryConfirmation() {
    this.hasError = false;
    this.submitting = true;
    try {
      await this.confirmSubscription();
    } finally {
      this.submitting = false;
    }
  }

  public goToBilling() {
    this.$router.push('/billing');
  }

  private async confirmSubscription() {
    const maxRetries = 10;
    let retryCount = 0;
    const retryDelay = 2000; // 2 seconds

    while (retryCount < maxRetries) {
      try {
        await this.userPackage.confirmed();
        break;
      } catch (error) {
        retryCount++;
        console.warn(`Subscription confirmation attempt ${retryCount} failed:`, error);

        // Track retry attempt (non-blocking)
        try {
          if (this.pricingTracker && retryCount > 0 && this.userPackage.chargeId) {
            this.pricingTracker.trackConfirmationRetry(
              this.userPackage.chargeId,
              retryCount
            );
          }
        } catch (trackingError) {
          // Silently fail - never block confirmation flow
          console.warn('[SubscriptionConfirm] Retry tracking failed:', trackingError);
        }

        try {
          await this.currentPackage.getCurrent();
        } catch {
          // skipped
        }

        if (this.currentPackage.id) {
          // Convert both to strings for comparison since API returns string chargeId
          const currentChargeId = String(this.currentPackage.chargeId);
          const targetChargeId = String(this.userPackage.chargeId);

          if (currentChargeId === targetChargeId) {
            break;
          }
        }

        if (retryCount < maxRetries) {
          // Wait before next retry
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        } else {
          console.error('Max retries reached for subscription confirmation');

          // Track confirmation failure (non-blocking)
          try {
            if (this.pricingTracker && this.userPackage.chargeId) {
              this.pricingTracker.trackSubscriptionConfirmationFailed({
                chargeId: this.userPackage.chargeId,
                errorMessage: 'Max retries reached for subscription confirmation',
                retryCount: retryCount,
                lastErrorCode: this.extractErrorMessage(error)
              });
            }
          } catch (trackingError) {
            // Silently fail - never block error display
            console.warn('[SubscriptionConfirm] Failure tracking failed:', trackingError);
          }

          // Set error state to show error message
          this.hasError = true;
          return;
        }
      }
    }

    // If we get here, subscription was confirmed successfully
    const confirmationTimeMs = Date.now() - this.confirmationStartTime;

    // Get current package details for tracking
    try {
      await this.currentPackage.getCurrent();
    } catch (e) {
      // Continue even if this fails
    }

    // Track TRUE CONVERSION - Subscription confirmed and activated (non-blocking)
    try {
      if (this.pricingTracker && this.currentPackage.id && this.userPackage.chargeId) {
        // Extract tracking data in a type-safe manner
        const trackingData = this.currentPackage.getTrackingData();

        this.pricingTracker.trackSubscriptionConfirmed({
          packageCode: trackingData.packageCode,
          packageName: trackingData.packageName,
          price: trackingData.price,
          isYearly: trackingData.isYearly,
          discountApplied: trackingData.discountApplied,
          discountCode: trackingData.discountCode,
          finalPrice: trackingData.finalPrice,
          billingPackageId: trackingData.billingPackageId
        }, {
          chargeId: this.userPackage.chargeId,
          subscriptionId: this.currentPackage.id?.toString() || 'unknown',
          retryCount: retryCount,
          confirmationTimeMs: confirmationTimeMs
        });
      }
    } catch (trackingError) {
      // Silently fail - never block successful subscription flow
      console.warn('[SubscriptionConfirm] Success tracking failed:', trackingError);
    }

    // Original event tracking
    try {
      const eventHandler = new EventHandler({
        store: this.store,
      });
      eventHandler.track('START_PAID_PACKAGE', {
        billing_package_id: this.userPackage.billingPackageId,
      });
      this.store.sendEvent('START_PAID_PACKAGE', {
        billing_package_id: this.userPackage.billingPackageId,
      });
    } catch (e) {
      // ignore this
    }
    await this.store.updateInstallationStatus();

    // Update store and emit event - catch errors to ensure navigation proceeds
    // try {
    //   await updateStoreAndEmitPackageChange();
    // } catch (error) {
    //   console.error('[SubscriptionConfirm] Failed to update store and emit package change:', error);
    //   // Don't rethrow - allow navigation to proceed even if store update fails
    // }

    // Force full page reload for both embedded and standalone modes.
    // redirectApp already branches on isEmbedded() — App Bridge open(_self)
    // when framed, window.location otherwise — so the destination stays stated
    // once. It is the single landing call every branch of this flow shares.
    redirectApp('/assistant/onboarding');
  }
}
